import { ColorBrandBlue1, ColorNeutralWhite, Spacing24 } from 'autogen/design-tokens/tokens';
import type { BrandDetailedModel } from 'autogen/swagger/all';
import { BrandHeadline, SeeAllHere } from 'autogen/translation-keys/trans-website-articleblock';
import InlineButton from 'components/generic/InlineButton';
import Picture from 'components/generic/picture/Picture';
import mapResponsiveImageModelToPictureProps from 'components/generic/picture/helpers/mappingHelper';
import { Typography } from 'components/generic/typography';
import { removeLinkTextDecoration } from 'modules/helpers/mixins';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PageAction from 'components/shared/Routing/PageAction';

// #region Styles
const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing24};
    align-items: center;
    text-align: center;    
    background-color: ${ColorNeutralWhite};
    padding: ${Spacing24};
`;

const Heading = styled(Typography.Headline)`
    color: ${ColorBrandBlue1};
`;

const BrandLogo = styled(Picture)`
    width: 100%;
    height: 100%;
    max-width: 160px;
    max-height: 60px;
`;

const StyledPageAction = styled(PageAction)`
    ${removeLinkTextDecoration}
`;

// #endregion Styles

function BrandCard({ brand }: { brand: NonNullable<BrandDetailedModel> }) {
    const { logo, name } = brand;
    const { t } = useTranslation();

    const Content = () => (
        <Wrapper>
            <Heading size="S" forwardedAs="h4">
                {t(BrandHeadline)}
            </Heading>
            {logo?.image.fallBackUrl && (
                <BrandLogo
                    {...mapResponsiveImageModelToPictureProps(logo.image, 160)}
                    alt={logo.image.altText || name}
                />
            )}
            <InlineButton fontWeight="semibold" as="span">
                {t(SeeAllHere)}
            </InlineButton>
        </Wrapper>
    );

    if (logo?.link?.linkAction) {
        return (
            <StyledPageAction action={{ $type: 'LinkActionType', action: logo.link.linkAction }}>
                <Content />
            </StyledPageAction>
        );
    }

    return <Content />;
}

export default withErrorBoundary(BrandCard, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
